import { User } from "users/user";

/**
 * Capability questions a view may ask of the signed-in user, answered from the grants the user
 * carries rather than from who they are (issue #4651).
 *
 * Nothing is threaded through the call: `useSessionUser` attaches the session's grants to the user,
 * so a view asks `user?.canAccessWork()` and is done. A User that never went through that hook —
 * a row in a table, a reviewer on a proposal — carries no grants and answers false to all of it,
 * which is the safe answer to a question nobody has the standing to answer.
 *
 * Views used to reach for identity — `isSuperUser`, `isCompanyManager` — wherever a grant was the
 * real rule, which spent the whole value of tiered authorization: two people holding the same
 * resource at the same scope got different screens because one of them had a different label. Every
 * such question now lives here, phrased as "may this session do X", so a view never has to know
 * which resource, scope, or access level stands behind the answer.
 *
 * Identity has not disappeared, but it survives in exactly one place below — {@link actsForALegalEntity} —
 * with the reason written down.
 */

/**
 * Whether this session transacts on behalf of a legal entity at all.
 *
 * This is the one question permissions cannot answer. `UsersPermissionsService.GrantAllSuperUserPermissionsAsync`
 * mints Global Read/Write grants for every work resource a super user's own record carries, so a
 * super user passes `includesAccessToProposals` and `includesAccessToWork` outright. Those grants
 * exist so the platform's operators can administer the system, not so they can negotiate inside a
 * tenant's Work and Proposals desks — a super user has no counterparty standing and nothing they
 * saved there would belong to anyone. So the desks that commit an organization to something ask
 * this first.
 *
 * Nothing else in the application should branch on `isSuperUser` to decide what a person may do.
 */
function actsForALegalEntity(user: User): boolean {
  return user.isSuperUser !== true;
}

/**
 * This user seen as the holder of these grants, for `useSessionUser` to hand a view.
 *
 * A derived object rather than a mutation: the session's own User is shared by everything that
 * reads the session, and writing to it during a render is exactly the impurity the React Compiler
 * refuses. `Object.create` puts the grants on a fresh object whose prototype is this user, so the
 * name, the id and every other capability still resolve through it and nothing is copied.
 */
User.prototype.withSessionPermissions = function (permissions): User {
  const holder: User = Object.create(this);
  holder.sessionPermissions = permissions ?? undefined;
  return holder;
};

User.prototype.canAccessCommunications = function (): boolean {
  return this.sessionPermissions?.includesAccessToCommunications === true;
};

User.prototype.canAccessWork = function (): boolean {
  return actsForALegalEntity(this) && this.sessionPermissions?.includesAccessToWork === true;
};

User.prototype.canAccessProposals = function (): boolean {
  return actsForALegalEntity(this) && this.sessionPermissions?.includesAccessToProposals === true;
};

/**
 * Whether this session may start a proposal draft.
 *
 * Read/Write at any scope, which is what `ProposalDraftingAdmission` asserts: any member may draft,
 * and sending is the bar that commits the firm — see {@link User.canSendNewProposals} (#5795).
 *
 * The exception is an officer whose firm is still onboarding. Their Need-to-Know grant exists so
 * they can work on the onboarding proposal itself, and the API refuses them a new draft on the
 * server-owned onboarding state, not on a grant. That state is on the user, so the question is
 * answered from it here and the Create button agrees with the 403 the API would give.
 */
User.prototype.canCreateProposals = function (): boolean {
  return (
    this.canAccessProposals() &&
    this.sessionPermissions?.includesReadWriteAccessToProposals === true &&
    !isStillOnboarding(this)
  );
};

/**
 * Whether this session may send a new proposal: the first submission of an opening offer, which the
 * API gates on Entity-Wide Read/Write because it commits the firm (#5795). A member without it can
 * still draft, and hands the draft over to its team leader, who then owns it and sends it (#6579).
 */
User.prototype.canSendNewProposals = function (): boolean {
  return (
    this.canAccessProposals() &&
    this.sessionPermissions?.includesEntityWideReadWriteAccessToProposals === true
  );
};

/** The same rule as `Session.isRestrictedOnboarding`, which reads this user's onboarding work too. */
function isStillOnboarding(user: User): boolean {
  const state = user.onboardingWork?.state;
  return !!state && state !== "Completed";
}

/**
 * Whether this session may watch how far the firms its organization invited have got.
 *
 * Entity-Wide Read-Only on proposals, which is the client-side half of the board's rule: the list
 * of firms an organization is bringing on is a supplier list, not the proposals a member happens to
 * sit on. Staff reach the same board through platform administration instead, across every client.
 *
 * A super user is excluded by `actsForALegalEntity` for the usual reason — their Global grants are
 * for running the platform, and they have no firms of their own to be onboarding.
 */
User.prototype.canViewFirmOnboardingStatus = function (): boolean {
  return actsForALegalEntity(this) && this.sessionPermissions?.includesEntityWideAccessToProposals === true;
};

/**
 * Whether this session may see which templates its organization's roll-out proposals are built
 * from.
 *
 * The same entity-wide read of proposals the firm onboarding board asks for, and for the same
 * reason: this configuration sets the terms every firm the organization onboards is offered, which
 * is an entity-level thing to know rather than something a member learns from the proposals they
 * happen to sit on. It is what `GET work/client-proposal-templates` asserts.
 */
User.prototype.canViewClientProposalTemplates = function (): boolean {
  return actsForALegalEntity(this) && this.sessionPermissions?.includesEntityWideAccessToProposals === true;
};

/**
 * Whether this session may change them.
 *
 * Two grants, because the endpoint asserts two. Entity-wide read/write on proposals sets the terms
 * of an offer, and entity-wide read/write on discounts prices it — and the discount here is applied
 * to every firm the organization onboards, so somebody who may build proposals but not price them
 * must not acquire that by coming through this door.
 */
User.prototype.canConfigureClientProposalTemplates = function (): boolean {
  return (
    this.canViewClientProposalTemplates() &&
    this.sessionPermissions?.includesEntityWideReadWriteAccessToProposals === true &&
    this.sessionPermissions?.includesEntityWideReadWriteAccessToDiscounts === true
  );
};

User.prototype.canAccessTeams = function (): boolean {
  return this.sessionPermissions?.includesAccessToTeams === true;
};

/**
 * `TeamTemplateAdministrationService.AssertCanCreateTeamTemplateAsync` asserts TeamsAccess at
 * Need-to-Know and Read/Write. A template is its creator's own record, so authoring one never
 * needed the entity-wide scope that reads the firm's other templates (#5771).
 */
User.prototype.canCreateTeamTemplates = function (): boolean {
  return this.canAccessTeams() && this.sessionPermissions?.includesReadWriteAccessToTeams === true;
};

User.prototype.canAccessFeeSchedules = function (): boolean {
  return this.sessionPermissions?.includesAccessToFeeSchedules === true;
};

/**
 * `FeeScheduleTemplate.CreateAsync` asserts FeeSchedulesAccess at Need-to-Know and Read/Write, so
 * the access level alone is decisive here — unlike proposals and team templates, any scope will do.
 */
User.prototype.canCreateFeeScheduleTemplates = function (): boolean {
  return actsForALegalEntity(this) && this.sessionPermissions?.includesReadWriteAccessToFeeSchedules === true;
};

/**
 * Whether this session may add a document: upload one to a work's Documents tab, to a proposal's
 * conflicts or policies, or anywhere else that creates one (#6595).
 *
 * `Document.CreateAsync` asserts General Documents at Need-to-Know and Read/Write
 * (`AssertGeneralDocumentAccessAsync(creator, Scope.NeedToKnow, AccessLevel.ReadWrite)`), so any
 * scope will do and the access level is decisive. A reader without it is shown the upload disabled,
 * with a way to request the access, rather than an upload the server refuses file by file.
 */
User.prototype.canUploadDocuments = function (): boolean {
  return this.sessionPermissions?.includesReadWriteAccessToGeneralDocuments === true;
};

User.prototype.canAccessKnowledgeBase = function (): boolean {
  return this.sessionPermissions?.includesAccessToKnowledgeBase === true;
};

User.prototype.canAuthorKnowledgeBase = function (): boolean {
  return this.sessionPermissions?.includesWriteAccessToKnowledgeBase === true;
};

User.prototype.canAccessMarketplace = function (): boolean {
  return this.sessionPermissions?.includesReadAccessToMarketplace === true;
};

User.prototype.canReadMarketplaceIndividual = function (): boolean {
  return this.sessionPermissions?.includesReadAccessToMarketplaceDirectory === true;
};

User.prototype.canSearchMarketplace = function (): boolean {
  return this.canAccessMarketplace() && this.sessionPermissions?.includesAccessToMarketplaceSearch === true;
};

User.prototype.canAccessNetwork = function (): boolean {
  return this.sessionPermissions?.includesAccessToNetwork === true;
};

/**
 * Administering the organization a session belongs to: its members, their permissions, and its
 * invitations. A super user administers the platform instead — see {@link User.canAdministerPlatform} —
 * and has no organization of their own to run.
 */
User.prototype.canAccessAdministration = function (): boolean {
  return actsForALegalEntity(this) && this.sessionPermissions?.includesAccessToAdministration === true;
};

/**
 * Whether this session may open a colleague's permissions page (`/permissions/people/{userId}`).
 * Every member may open their own, read-only; reading anybody else's asks for Member Permissions.
 */
User.prototype.canReadMemberPermissions = function (): boolean {
  return actsForALegalEntity(this) && this.sessionPermissions?.includesReadAccessToMemberPermissions === true;
};

/**
 * Whether this session may grant a colleague signing authority, as a team leader handing the team
 * over to somebody who lacks it does (#5836), or an administrator does from the Organization ›
 * Members row (#6164). The server asks for Member Permissions at Entity-Wide and Read/Write, the
 * authority the permissions page itself asks for, so a session without it is not offered the grant.
 */
User.prototype.canGrantSigningAuthority = function (): boolean {
  return actsForALegalEntity(this) && this.sessionPermissions?.includesWriteAccessToMemberPermissions === true;
};

/**
 * Whether this session may open the organization's permission templates, and read, apply, create,
 * change or delete any of them (#6826). A template belongs to the organization, not to the person
 * who made it. Every template endpoint asserts Member Permissions at Entity-Wide and Read/Write, the
 * authority the permissions page asks for to change a colleague's grants: a template writes only
 * grants its administrators could write by hand.
 */
User.prototype.canManagePermissionTemplates = function (): boolean {
  return actsForALegalEntity(this) && this.sessionPermissions?.includesWriteAccessToMemberPermissions === true;
};

/**
 * Whether this session may read its organization's access requests and approve or deny them
 * (#6596), on the Permissions page's Requests.
 *
 * `AccessRequestService.GetForGrantorAsync` asserts Member Permissions at Entity-Wide and Read/Write,
 * the authority the permissions page asks for to change a colleague's grants: answering a request is
 * granting the access or not. Deciding one request also needs the access asked for, which the API
 * reports per request (`callerMayDecide`), so this opens the list and each row says what it allows.
 */
User.prototype.canDecideAccessRequests = function (): boolean {
  return actsForALegalEntity(this) && this.sessionPermissions?.includesWriteAccessToMemberPermissions === true;
};

/**
 * Whether this session may invite colleagues into its organization, one at a time or as a roster.
 *
 * Narrower than {@link User.canAccessAdministration}, which Member Permissions or read-only Member
 * Invitations also satisfy. Sending an invitation asserts Member Invitations at Entity-Wide and
 * Read/Write — the single `POST legal-entities/{id}/user-entity-member-invitations` and its `/bulk`
 * roster alike (`BulkEntityMemberInvitationService`), because entering a roster is the same
 * authority as entering one person repeatedly. Gating the roster on the umbrella capability led an
 * administrator of permissions to a grid where every submission was refused.
 */
User.prototype.canInviteColleagues = function (): boolean {
  return this.canAccessAdministration() && this.sessionPermissions?.includesWriteAccessToMemberInvitations === true;
};

/**
 * The one capability that is genuinely identity and not a grant. Platform administration is not a
 * resource any organization can hand out: it is the register a super user is entered into, and the
 * API gates the super-user endpoints on that register rather than on a stored permission.
 */
User.prototype.canAdministerPlatform = function (): boolean {
  return this.isSuperUser === true;
};
